import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type {
  TimedQuestClearCondition,
  TimedQuestDef,
  TimedQuestRepeatPolicy,
} from '../../../master-data/types/timed-quest.types.ts';

// timed_quest — 시간제한 퀘스트. clearCondition/rewards 는 JSONB 이므로
// kind:'json' 으로 통째 보존한다(glory-gacha 와 동일 방식).
export const TimedQuestSchema = defineSchema({
  name: 'serverTimedQuests',
  fields: [
    ['id', 'number'],
    ['triggerEvent', 'string'],
    ['triggerParam', 'string?'],
    ['delayMinutes', 'number'],
    ['durationMinutes', 'number'],
    ['clearCondition', { kind: 'json' }],
    ['rewards', { kind: 'json' }],
    ['retryLimit', 'number'],
    ['repeatPolicy', 'string'],
    ['isActive', 'boolean'],
    ['order', 'number'],
    // append-only (튜플 와이어 순서 계약) — 신규 필드는 항상 끝에 붙인다.
  ],
  lookupKeys: ['id'],
});

export interface RawTimedQuestRow {
  id: number;
  title: string;
  description: string;
  trigger_event: string;
  trigger_param: string | null;
  delay_minutes: number | null;
  duration_minutes: number;
  clear_condition: TimedQuestClearCondition | null;
  rewards: Record<string, number> | null;
  retry_limit: number | null;
  repeat_policy: string;
  is_active: boolean;
  display_order: number | null;
}

const REPEAT_POLICIES: TimedQuestRepeatPolicy[] = ['ONCE', 'DAILY', 'UNLIMITED'];

/**
 * 알 수 없는 repeat_policy 는 ONCE 로 폴백한다 — 오타 하나가 UNLIMITED 로 해석돼
 * 무한 보상 구멍이 되는 것보다, 가장 보수적인 정책으로 닫는 편이 안전하다.
 */
function normalizeRepeatPolicy(value: string): TimedQuestRepeatPolicy {
  const upper = String(value ?? '').toUpperCase() as TimedQuestRepeatPolicy;
  return REPEAT_POLICIES.includes(upper) ? upper : 'ONCE';
}

export function timedQuestFromRaw(row: RawTimedQuestRow): TimedQuestDef {
  const def: TimedQuestDef = {
    id: Number(row.id),
    title: String(row.title),
    description: String(row.description),
    triggerEvent: String(row.trigger_event),
    delayMinutes: Number(row.delay_minutes ?? 0),
    durationMinutes: Number(row.duration_minutes),
    clearCondition: row.clear_condition ?? {},
    rewards: row.rewards ?? {},
    retryLimit: Number(row.retry_limit ?? 0),
    repeatPolicy: normalizeRepeatPolicy(row.repeat_policy),
    isActive: Boolean(row.is_active),
    order: Number(row.display_order ?? 0),
  };
  const param = String(row.trigger_param ?? '').trim();
  if (param) def.triggerParam = param;
  return def;
}

registerCategorySchema('serverTimedQuests', TimedQuestSchema);
